(ns frontend.react.virtualized.scrollable-grid.components
  (:require
   [ui.components.radix-ui.avatar.view :as avatar]
   [ui.util :as uu]
   [clojure.string :as string]
   [common.attribute :as attribute]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.actions.named-view :as named-view-actions]
   [frontend.react.virtualized.scrollable-grid.events :as events]
   [frontend.state.current-session :as session]
   [frontend.ui.component.search-input.view :refer [search-input]]
   [frontend.utils :as fu]
   [frontend.view.components.switch-toggle :as switch]
   [frontend.view.modals.components.error-layer :as error-layer]
   [potpuri.core :refer [fn-> fn->> index-by]]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.input.checkbox :as checkbox]
   [ui.components.radix-ui.popover.view :as popover]
   [ui.editors.data-input :as data-input]))

(defn field-listing
  [{:keys [entity-type fields selected-field select-field!] :as args}]
  (let [initial-rows 11
        tenant-schema @session/tenant-schema
        local-state (r/atom {:show-all? false})]
    (fn [{:keys [entity-type fields selected-field select-field!] :as args}]
      (let [[entity-fields rest-fields] (->> fields
                                             ((juxt filter remove) keyword?))
            grouped-fields (->> rest-fields
                                (group-by first)
                                seq)
            {:keys [show-all?]} @local-state
            max-rows (->> (concat [entity-fields]
                                  (mapcat second grouped-fields))
                          (map count)
                          (apply max))
            column-count (+ (count grouped-fields) 1)]
        [:div
         [:div
          {:class             (uu/cm "grid gap-4"
                                     (str "grid-cols-" column-count))}
          [:div
           {:class "flex flex-col gap-1"}
           (when (> column-count 1)
             [:div
              {:class "pb-3 font-bold"}
              (loc/tr :grouped-properties-heading [(loc/tr entity-type)])])
           (for [f (if show-all?
                     entity-fields
                     (take initial-rows entity-fields))]
             ^{:key (str "entity-field-" f)}
             [:div
              {:class-name (uu/cm "p-2 rounded cursor-pointer hover:bg-base-regular"
                                  (when (= f selected-field)
                                    "bg-base-regular"))
               :on-click   (fn [_]
                             (select-field! f))}
              (attribute/attribute-label tenant-schema f)])]
          (for [[entity-field ref-fields] grouped-fields]
            ^{:key (str "grouped-by-fields-" entity-field)}
            [:div
             {:class "flex flex-col gap-1"}
             [:div
              {:class "pb-3 font-bold"}
              (loc/tr :grouped-properties-heading
                      [(attribute/attribute-label tenant-schema entity-field)])]
             (for [f (if show-all?
                       ref-fields
                       (take initial-rows ref-fields))]
               ^{:key (str entity-field "-field-" f)}
               [:div
                {:class-name (uu/cm "p-2 rounded cursor-pointer hover:bg-base-regular"
                                    (when (= f selected-field)
                                      "bg-base-regular"))
                 :on-click   (fn [_]
                               (select-field! f))}
                (attribute/attribute-label tenant-schema (last f))])])]
         (when (and (not show-all?)
                    (> max-rows initial-rows))
           [:div
            {:class      "pt-2 block text-center underline hover:text-base-light-text-accent cursor-pointer"
             :on-click   (fn [_]
                           (swap! local-state assoc :show-all? true))}
            (loc/tr :show-more-ellipsis)])]))))

(defn filter-selector
  [{:keys [filters filter-sidenav-minimized? toggle-filter-sidenav!]}]
  (let [filters-count (count filters)
        show-tooltip? filter-sidenav-minimized?
        ;;highlight if number of filter > 0 AND quick-filters sidenav is closed
        highlight? (and filter-sidenav-minimized?
                        (> filters-count 0))]
    (when
     (and show-tooltip?)
      [button/secondary-open
       {:on-click (fn [e]
                    (toggle-filter-sidenav!))
        :tooltip  (loc/tr :filter)}
       [:div
        {:class "flex items-center gap-2"}
        [:i.fal.fa-search]
        [:span
         {:class "hidden md:block truncate whitespace-nowrap"}
         (if (= filters-count 1)
           [:span
            {:class "flex gap-2"}
            (loc/tr :filter)
            [:span
             {:class "h-5 px-2 flex items-center justify-center text-xs font-semibold text-base-light-text bg-complement-regular rounded-full"}
             filters-count]]
           [:span
            {:class "flex gap-2"}
            (loc/tr :filter-plural)
            [:span
             {:class "h-5 px-2 flex items-center justify-center text-xs font-semibold text-base-light-text bg-complement-regular rounded-full"}
             filters-count]])]]])))

(defn save-current-view
  [{:keys [save-current-view!]}]
  (let [local-state (r/atom {})
        modify! (fn [k v] (swap! local-state assoc k v))
        ;;set-edit! (partial modify! :edit?)
        modify-name! (fn [v]
                       (modify! :name v))]
    (fn [{:keys [save-current-view!]}]
      (let [{:keys [name]} @local-state
            save-button-disabled? (->> name
                                       string/blank?)]
        [:div
         {:class "flex flex-col gap-3"}
         [:div
          {:class "font-bold"}
          (loc/tr :need-current-view-often)]
         [:div
          {:class "text-base-light-text-paler"}
          (loc/tr :save-current-view-info)]
         [:div
          {:class "grid grid-cols-10 gap-3 items-center"}
          [:div
           {:class "col-span-8"}
           [data-input/view
            {:data-type   :data.type/text-simple
             :class       "w-full"
             :placeholder (loc/tr :type-a-view-name)
             :value       name
             :on-change   modify-name!}]]
          [:div
           {:class "col-span-2"}
           [button/sm-primary-solid
            {:disabled? save-button-disabled?
             :tooltip   (when save-button-disabled?
                          (loc/tr :name-cannot-be-blank))
             :on-click  (fn [_]
                          (save-current-view! name)
                          ;;TODO this inside callback
                          (events/hide-popover! local-state))}
            [:span (loc/tr :save)]]]]]))))

(defn named-views-popover
  [{:keys [named-views load-view! delete-view! share-view! local-state accounts-with-view-access
           current-view matching-view saving-disabled?]
    :as   args}]
  [:div
   [:div
    {:class "md:flex gap-6"}
    [:div
     {:class "flex flex-col gap-3"}
     [:div
      {:class "pb-1 text-base-light-text font-bold"}
      (loc/tr :select-view)]
     (doall
      (for [named-view (events/sort-named-views named-views)
            :let [id (:db/id named-view)
                  name (:named-view/name named-view)]]
        ^{:key (str "name-view-" id)}
        [:div
         {:class-name (fu/cs "flex gap-2 items-center text-base-light-text hover:text-base-light-text-accent truncate"
                             ""
                             "cursor-pointer"
                             (when (= id (:db/id matching-view))
                               "selected"))
          :on-click   (fn [_]
                        (load-view! id)
                        (events/hide-popover! local-state))}
         [:span name]
         (when (int? id)
           [:div
            {:class "flex gap-1"}
            (when (and (not (session/is-read-only-user?))
                       (->> accounts-with-view-access
                            (cu/remove-by :db/id @session/account-id)
                            (seq)))
              [button/square-secondary-open
               {:on-click (fn [e]
                            (.preventDefault e)
                            (.stopPropagation e)
                            (share-view! id))}
               [:i.fal.fa-share]])
            [button/square-secondary-open
             {:on-click (fn [e]
                          (.preventDefault e)
                          (.stopPropagation e)
                          (events/hide-popover! local-state)
                          (delete-view! id))}
             [:i.fal.fa-trash-alt]]])]))]
    (when (and (not matching-view) (not saving-disabled?))
      [save-current-view args])]])

(defn account-selector
  [{:keys [local-state account-id account]}]
  (let [{:keys [share-with]} @local-state]
    [:label
     {:class "flex flex-row gap-2 items-center"}
     [checkbox/view
      {:value     (boolean (some #{account-id} share-with))
       :on-change (fn [e]
                    (swap! local-state assoc
                           :share-with
                           (if (contains? share-with account-id)
                             (disj share-with account-id)
                             (conj share-with account-id)))
                    (deref local-state))}]
     [:div
      [avatar/view
       {:size :small
        :font-size :small
        :fallback    (str (first (:account/first-name account))
                          (first (:account/last-name account)))
        :image-url (:account/picture-url account)}]]
     [:span (entity/display-string account)]]))

(defn accounts-selector-list
  [{:keys [local-state accounts parent-id]}]
  (let [{:keys [share-with]} @local-state
        accounts (->> accounts
                      (sort-by (fn [a]
                                 (cu/str-trim-lcase (:account/first-name a)))))]
    [:ul
     {:class "py-2 scroll flex flex-column gap-2"}
     (doall
      (for [account accounts
            :let [account-id (:db/id account)]]
        ^{:key (str "share-" parent-id "-" account-id)}
        [:li
         [account-selector
          {:local-state local-state
           :account-id  account-id
           :account     account}]]))]))

(defn trigger-account-search
  [{:keys [local-state accounts search-text]}]
  (let [old-timeout-handle (:search-timeout @local-state)]
    (when old-timeout-handle
      (js/clearTimeout old-timeout-handle))
    (swap! local-state assoc
           :search-timeout
           (fu/timeout 500
                       (fn []
                         (let [l-case-search (cu/str-trim-lcase search-text)
                               matching-accounts (->> accounts
                                                      (filter (fn [a]
                                                                (-> a
                                                                    (entity/display-string)
                                                                    (cu/str-trim-lcase)
                                                                    (string/includes? l-case-search)))))]
                           (swap! local-state assoc :matching-accounts matching-accounts)
                           (deref local-state)))))
    (deref local-state)))

(defn share-popover
  [{:keys [local-state accounts-with-view-access]}]
  (let [{:keys [share-view-id share-with search-text matching-accounts]} @local-state
        other-accounts (->> accounts-with-view-access
                            (cu/remove-by :db/id @session/account-id))
        all-selected? (= (count other-accounts)
                         (count share-with))]
    [:div
     {:class "flex flex-column gap-2 overflow-hidden"}
     [:p
      {:class "font-semibold"}
      (loc/tr :share-view)]
     [:p
      {:class "text-xs"}
      (loc/tr :named-view-share-info-text)]
     (when (> (count other-accounts) 6)
       [:<>
        [search-input
         {:value     search-text
          :on-change (fn [v]
                       (swap! local-state assoc :search-text (str v))
                       (trigger-account-search {:local-state local-state
                                                :accounts    other-accounts
                                                :search-text (str v)}))}]
        (when-not (cu/blank-value? search-text)
          [:div
           [:p
            (loc/tr :search-results)]
           [accounts-selector-list
            {:local-state local-state
             :accounts    matching-accounts
             :parent-id   "named-view-share-matches"}]])])

     [:label
      {:class "flex flex-row gap-2"}
      [checkbox/view
       {:value     all-selected?
        :on-change (fn [_e]
                     (swap! local-state assoc
                            :share-with
                            (if all-selected?
                              #{}
                              (->> other-accounts
                                   (map :db/id)
                                   (set))))
                     (deref local-state))}]
      [:span (loc/tr :all-members-with-count [(count other-accounts)])]]

     [accounts-selector-list
      {:local-state local-state
       :accounts    other-accounts
       :parent-id   "named-view-share-all"}]
     [:div
      {:class "flex flex-row gap-2 justify-items-stretch"}
      [button/sm-secondary-hollow
       {:class    "flex-auto"
        :on-click (fn [_]
                    (swap! local-state dissoc :share-view-id :search :share-with))}
       (loc/tr :cancel)]
      [button/sm-primary-solid
       {:disabled? (empty? share-with)
        :class     "flex-auto"
        :on-click  (fn [_]
                     (swap! local-state assoc :processing true)
                     (named-view-actions/*share-named-view->
                      {:view-id    share-view-id
                       :share-with share-with}
                      (fn [resp]
                        (swap! local-state dissoc :processing)
                        (events/hide-popover! local-state))
                      (fn [err]
                        (swap! local-state dissoc :processing)
                        (swap! local-state assoc :error-while-sharing? true))))}
       (loc/tr :share-view)]]]))

(defn processing-popover
  []
  [:div
   {:class "flex flex-col"}
   [:i.fal.fa-spinner.fa-pulse.fa-5x]])

(defn named-view-listing-popover
  [{:keys [custom-view? saving-disabled?] :as args}]
  [named-views-popover args])

(defn named-view-selector
  []
  (let [local-state (r/atom {:show-tooltip? true})
        target-state (atom {})]
    (fn [{:keys [named-views current-view load-view! delete-view! save-current-view!
                 accounts-with-view-access saving-disabled?]}]
      (let [{:keys [show? show-tooltip? share-view-id processing? error-while-sharing?]}
            @local-state
            target (:target @target-state)
            cv (cu/remove-empty-vals current-view)
            matching-view (->> named-views
                               (filter (fn-> :named-view/settings cu/remove-empty-vals (= cv)))
                               first)
            custom-view? (nil? matching-view)
            view-text (if custom-view?
                        (loc/tr :custom-view)
                        (:named-view/name matching-view))
            tooltip-text view-text]

        (when
         (and tooltip-text show-tooltip?)

          [popover/view
           {:positions      ["bottom"]
            :open?          show?
            :on-open-change (fn [open?]
                              (swap! local-state assoc :show? open?))}
           [button/secondary-open
            {:tooltip tooltip-text
             :class   "mb-0 !py-0"}

            [:div
             {:class "flex items-center gap-3"}
             [:i.fal.fa-eye]
             [:span
              {:class "hidden md:block truncate whitespace-nowrap"}
              view-text]]]

           [:div
            {:class "max-w-[320px] lg:max-w-[600px] m-0 flex p-3 rounded text-sm text-base-light-text"}

            (when show?
              [:div
               {:target target}
               (cond
                 error-while-sharing?
                 (error-layer/generic-error nil)

                 processing?
                 (processing-popover)

                 share-view-id
                 ;; if these are components [ the overlay target / popup does not go to right place
                 (share-popover {:local-state               local-state
                                 :accounts-with-view-access accounts-with-view-access})

                 :else
                 (named-view-listing-popover {:local-state               local-state
                                              :custom-view?              custom-view?
                                              :named-views               named-views
                                              :current-view              current-view
                                              :matching-view             matching-view
                                              :accounts-with-view-access accounts-with-view-access
                                              :load-view!                load-view!
                                              :delete-view!              delete-view!
                                              :share-view!               (fn [view-id]
                                                                           (swap! local-state assoc
                                                                                  :share-view-id view-id
                                                                                  :share-with #{})
                                                                           (deref local-state))
                                              :save-current-view!        save-current-view!
                                              :saving-disabled?          saving-disabled?}))])]])))))

(defn pipelines-search
  [local-state]
  [:div
   {:class "mb-2"}
   [:div
    [data-input/view
     {:data-type   :data.type/text-simple
      :placeholder (loc/tr :search-default-value)
      :value       (:pipeline-search @local-state)
      :on-change   (fn [new-value]
                     (swap! local-state assoc :pipeline-search new-value))}]
    [:i
     {:class "fal fa-search ml-2"}]]])

(defn pipelines-popover
  [{:keys [filtered-pipelines pipelines select-pipeline!
           selected-pipeline-id local-state]
    :as   args}]
  [:div
   [:div
    (when (< 10 (count pipelines))
      [pipelines-search local-state])

    (if (empty? filtered-pipelines)
      [:div (loc/tr :no-pipelines-found)]
      [:div
       {:class "flex flex-col gap-1"}
       (doall
        (for [pipeline filtered-pipelines
              :let [id (:db/id pipeline)
                    pipeline-name (entity/display-string pipeline)]]
          ^{:key (str "name-view-" id)}
          [:div
           {:class-name (fu/cs "p-2 cursor-pointer rounded hover:bg-base-regular"
                               "clickable"
                               (when (= id selected-pipeline-id)
                                 "bg-base-regular"))
            :on-click   (fn [_]
                          (select-pipeline! id)
                          (events/hide-popover! local-state))}
           [:span pipeline-name]]))])]])

(defn pipeline-selector
  [{:keys [pipelines select-pipeline! view-text selected-pipeline-id]}]
  (let [local-state (r/atom {:show-tooltip? true})
        target-atom (atom nil)]
    (fn [{:keys [pipelines select-pipeline! view-text selected-pipeline-id]}]
      (let [{:keys [show? show-tooltip? pipeline-search]} @local-state
            filtered-pipelines (events/filter-pipelines-by-search pipeline-search pipelines)]
        [popover/view
         {:positions      ["bottom"]
          :open?          (:popover-open? @local-state)
          :on-open-change (fn [open?]
                            (js/console.log "Open popover? " open? " : " (:popover-open? @local-state))
                            (swap! local-state assoc :popover-open?
                                   (not (:popover-open? @local-state))))}
         [:div
          {:class "flex items-center gap-2"}
          [:i.fas.fa-code-branch]
          [:span
           {:class "font-semibold hidden md:inline-block text-ellipsis"}
           view-text]]
         [:div
          {:id         "sort-selector-popover"
           :class      "max-w-[43rem] max-h-[80vh]"}
          [pipelines-popover
           {:pipelines            pipelines
            :filtered-pipelines   filtered-pipelines
            :select-pipeline!     select-pipeline!
            :selected-pipeline-id selected-pipeline-id
            :local-state          local-state}]]]))))

(defn sorted-popover
  [{:keys [entity-type sort-direction sort-by-key target fields set-sort-by!]}]
  (let [tenant-schema @session/tenant-schema
        fs (registry/get-field-schema tenant-schema sort-by-key)]
    [:div
     [:div
      (when sort-by-key
        [:div
         {:class "hidden"
          :on-click (fn [_]
                      (set-sort-by! nil nil))}
         (loc/tr :clear-sort)])
      (when sort-by-key
        [:div
         {:class "flex gap-2"}
         [:div
          {:class "flex gap-2 items-center"}
          [:span
           {:class "text-base-light-text text-xs items-center"}
           (loc/tr :set-sort-direction)]
          [:span
           [switch/view
            {:options (for [dir ["ASC" "DESC"]]
                        {:key        dir
                         :label      (cu/dir-label fs dir)
                         :selected?  (= dir sort-direction)
                         :on-select! (fn [] (set-sort-by! sort-by-key dir))})}]]]])
      [field-listing
       {:entity-type    entity-type
        :key            "field-listing"
        :fields         fields
        :selected-field sort-by-key
        :select-field!  (fn [field] (set-sort-by! field (or sort-direction "ASC")))}]]]))

(defn sort-selector
  []
  (let [local-state (r/atom {:show-tooltip? true})
        tenant-schema @session/tenant-schema
        target-state (atom {})]
    (fn [{:keys [entity-type view-settings sortable-fields set-sort-by!]}]
      (let [{:keys [show? show-tooltip?]} @local-state
            target (:target @target-state)
            {:keys [sort-direction]} view-settings
            sort-by-key (:sort-by view-settings)
            tooltip-text (when sort-by-key
                           (loc/tr :sorted-by-attribute
                                   [(attribute/attribute-label tenant-schema sort-by-key)]))]

        [popover/view
         {:open?          show?
          :as-child?      true
          :on-open-change (fn [open?]
                            (swap! local-state assoc :show? open?))}

         [button/secondary-open
          {:tooltip  tooltip-text
           :on-click (fn [e]
                       (swap! local-state assoc :show? (not (:show? @local-state)))
                       (fu/stop-n-prevent e))}
          [:div
           {:class "flex items-center gap-2"}
           [:i.fal.fa-exchange.fa-rotate-90]
           (if sort-by-key
             [:span
              {:class "hidden md:inline-block text-ellipsis"}
              (loc/tr :sorted)]
             [:span
              {:class "hidden md:inline-block text-ellipsis"}
              (loc/tr :sort)])]]

         [:div
          {:id         "sort-selector-popover"
           :class      "p-4 max-w-[43rem] max-h-[80vh]"}
          [sorted-popover
           {:set-sort-by!   set-sort-by!
            :fields         sortable-fields
            :entity-type    entity-type
            :sort-by-key    sort-by-key
            :sort-direction sort-direction}]]]))))

(defn grouped-by-popover
  [{:keys [entity-type grouped-by grouped-by-fields set-grouped-by!
           local-state]}]
  [:div
   [:div
    {:class "pb-2"}
    (loc/tr :group-items-by)]

   [field-listing
    {:entity-type    entity-type
     :fields         grouped-by-fields
     :selected-field grouped-by
     :select-field!  (fn [field]
                       (set-grouped-by! field)
                       (events/hide-popover! local-state))}]])

(defn grouped-by-selector
  [{:keys [entity-type view-settings default-grouped-by grouped-by-fields set-grouped-by!]}]
  (let [local-state (r/atom {:show-tooltip? true})
        tenant-schema @session/tenant-schema]
    (fn [{:keys [entity-type view-settings default-grouped-by grouped-by-fields set-grouped-by!]}]
      (let [{:keys [popover-open?]} @local-state
            {:keys [grouped-by]} view-settings
            grouped-by (or grouped-by
                           default-grouped-by)
            tooltip-text (loc/tr :grouped-by-attribute
                                 [(attribute/attribute-label tenant-schema grouped-by)])]
        (when-not (and (= (count grouped-by-fields) 1)
                       (= grouped-by (first grouped-by-fields)))
          [popover/view
           {:open?          popover-open?
            :as-child?      true
            :on-open-change (fn [_open?]
                              (swap! local-state assoc :popover-open? (not (:popover-open? @local-state))))}
           [button/secondary-open
            {:tooltip tooltip-text}
            [:div
             {:class "flex items-center gap-2"}
             [:i.fal.fa-database]
             [:span
              {:class "hidden md:block truncate whitespace-nowrap"}
              (attribute/attribute-label tenant-schema grouped-by)]]]

           [:div
            {:id         "sort-selector-popover"
             :class      "p-4 max-w-[43rem] max-h-[80vh]"}
            [grouped-by-popover
             {:set-grouped-by!   set-grouped-by!
              :local-state       local-state
              :entity-type       entity-type
              :grouped-by-fields grouped-by-fields
              :grouped-by        grouped-by}]]])))))

(defn view-mode-tooltip
  [board-mode?]
  (let [editable-grid-on? (and (session/has-feature-access? :feature/editable-grid)
                               @session/editable-grid?)
        grid-mode-tooltip-text (if editable-grid-on?
                                 (loc/tr :switch-to-editable-grid)
                                 (loc/tr :switch-to-grid-mode))]
    (r/as-element (if board-mode?
                    grid-mode-tooltip-text
                    (loc/tr :switch-to-kanban)))))

(defn collapse-all-tooltip
  []
  ;; TODO - @Hanna + @Miko, please fix
  (r/as-element
   [:div "TODO"]
   #_[rbs/Tooltip
      {:class "in"
       :id    "collapse-tooltip"}
      (loc/tr :collapse-all)]))

(defn expand-all-tooltip
  []
  ;; TODO - @Hanna + @Miko, please fix
  (r/as-element
   [:div "TODO"]
   #_[rbs/Tooltip
      {:class "in"
       :id    "expand-tooltip"}
      (loc/tr :expand-all)]))
